<script setup lang="ts">
import { computed, type Ref, type CSSProperties } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { t } from '@/locales/i18n'
import { numFixedUnit } from '@gto/helpers/number-formatting'

import GwBox from '@/components/lib/GwBox/GwBox.vue'
import * as R from 'remeda'
import { refDebounced } from '@vueuse/core'
import type { GwIconVariants } from '@/components/lib/GwIcon/GwIcon.vue'
import type { StatRowProps } from '@/components/hhv3/Analyzer/StatsBoxes/StatRow.vue'
import type { TextVariants } from '@/components/lib/base/GwText/gwTextVariants'
import { useScreenMix } from '@/composables/screenMix'
import type { SelectFilterParams } from '@/components/hhv3/Analyzer/StatsTable/SimpleTable.vue'
import { numFixed } from '@/helpers/helpers'
import type { Filter } from '../../../../types/api/reports'
import { generateFilterParams } from '../../../../store/api/gql/hands/utils'
import {
  getHandsStatistics,
  handsStatisticsQueryKey,
} from '../../../../store/api/gql/hands/queries'
import StatsBox from './StatsBox.vue'
import SimpleTable from '../StatsTable/SimpleTable.vue'
import ScoreBreakdown from './ScoreBreakdown.vue'

const { max620 } = useScreenMix()

const props = defineProps<{
  filters: Filter[]
}>()

const emit = defineEmits<{
  'cell:click': [SelectFilterParams]
}>()

const filterParams = computed(() => generateFilterParams(props.filters))
const queryKey = computed(() => [...handsStatisticsQueryKey, filterParams.value])

const { data, isLoading, isFetching } = useQuery({
  queryKey: refDebounced(queryKey, 300),
  queryFn: getHandsStatistics(filterParams),
  placeholderData: R.identity,
  refetchOnWindowFocus: false,
})

const getEvLossValue = (value: number) => numFixed(value * 100, max620.value && value > 1 ? 1 : 2)

const stats: Ref<
  {
    title: string
    icon: string
    iconVariant: GwIconVariants
    value: string | number
    unit?: string
    bottomText?: {
      value: string
      variant: TextVariants
    }
    chart?: {
      id: string
      value?: number[]
    }
    extra?: StatRowProps[]
    style?: CSSProperties
  }[]
> = computed(() => {
  const handStats = data?.value?.hand_statistics || {
    avg_gto_score: 0,
    count_hands: 0,
    total_moves: 0,
    avg_ev_loss: 0,
    total_ev_loss: 0,
    avg_ev_loss_as_pot: 0,
    avg_frequency_difference: 0,
    avg_ev_loss_per_mistake: 0,
  }
  return [
    {
      title: t('hhv3.gto_score'),
      icon: 'mdi-speedometer-slow',
      iconVariant: 'primary-v2_bgLight',
      value: numFixedUnit(handStats.avg_gto_score * 100, 'gto_score').toString(),
      chart: {
        id: 'gto_score_trend',
        value: data?.value?.gto_score_trend,
      },
      unit: '%',
      style: {
        maxWidth: '22rem',
      },
    },
    {
      title: t('common.hands'),
      icon: 'mdi-cards-playing-outline',
      iconVariant: 'white_bgLight',
      value: handStats.count_hands,
      bottomText: {
        value: t(`hhv3.xxx_moves`, { num: handStats.total_moves }),
        variant: 'text_grey_medium_500',
      },
      style: {
        maxWidth: '22rem',
      },
    },
    {
      title: t('hhv3.frequency_diff'),
      icon: 'mdi-percent',
      iconVariant: 'primary-v2_bgLight',
      value: numFixedUnit(handStats.avg_frequency_difference * 100, 'frequency_diff'),
      unit: '%',
      style: {
        maxWidth: max620.value ? '' : '22rem',
      },
    },
    {
      title: t('hhv3.avg_ev_loss'),
      icon: 'mdi-chart-bell-curve-cumulative',
      iconVariant: 'white_bgLight',
      value: getEvLossValue(handStats.avg_ev_loss),
      unit: 'bb',
      bottomText: { value: t(`hhv3.per_num_hands`, { num: 100 }), variant: 'text_grey_medium_500' },
      extra: [
        {
          value: getEvLossValue(handStats.avg_ev_loss_as_pot),
          unit: 'pot%',
          bottomText: {
            value: t(`hhv3.per_num_hands`, { num: 100 }),
            variant: 'text_grey_medium_500',
          },
        },
        {
          unit: t('common.bb'),
          value: getEvLossValue(handStats.avg_ev_loss_per_mistake),
          bottomText: {
            value: t('hhv3.per_num_mistakes', { num: 100 }),
            variant: 'text_grey_medium_500',
          },
        },
      ],
      style: {
        maxWidth: max620.value ? '' : '32rem',
      },
    },
  ]
})
</script>

<template>
  <GwBox
    variant="box_gradient180"
    class="f-column flex-1"
    style="
      border-radius: 0;
      padding: var(--app-pad-4);
      gap: var(--app-gap-4);
      --ideal-left-part-width: clamp(50%, 58rem, 100%);
    "
  >
    <div
      class="statsboxes f-wrap"
      style="gap: var(--app-gap-4)"
    >
      <div
        class="statsboxes_first d-flex flex-1"
        style="min-width: clamp(50%, 58rem, 100%); gap: var(--app-gap-4)"
      >
        <StatsBox
          v-for="stat in stats.slice(0, 3)"
          :key="stat.title"
          class="flex-1 statsboxes_item"
          :loading="isLoading || isFetching"
          v-bind="stat"
          :style="stat.style"
        />
      </div>

      <div
        class="statsboxes_last d-flex flex-1"
        style="gap: var(--app-gap-4)"
      >
        <StatsBox
          v-for="stat in stats.slice(3)"
          :key="stat.title"
          class="flex-1 statsboxes_item"
          :loading="isLoading || isFetching"
          v-bind="stat"
          :style="stat.style"
        />
      </div>
    </div>
    <div
      class="d-flex f-wrap"
      style="gap: var(--app-gap-4)"
    >
      <SimpleTable
        :filterParams
        class="flex-1"
        style="min-width: var(--ideal-left-part-width)"
        @cell:click="emit('cell:click', $event)"
      />
      <GwBox
        variant="box_gradient180_bordered"
        class="stats_chart flex-1"
        style="
          min-width: clamp(30%, 32rem, 100%);
          padding: var(--chart-padding);
          border-radius: var(--app-rad-4);
        "
        :loading="isLoading || isFetching"
      >
        <ScoreBreakdown
          class="flex-1"
          :filters
          :gtoScore="data?.hand_statistics.avg_gto_score"
        />
      </GwBox>
    </div>
  </GwBox>
</template>

<style scoped lang="scss">
.stats_chart {
  --chart-padding: var(--app-pad-6);
}
.statsboxes {
  display: flex;
  &_item {
    flex: 1;
    min-width: 12rem;
  }
}

@media (max-width: 1023px) {
  .stats_chart {
    --chart-padding: 0 0 0 0;
  }
  .statsboxes_first {
    flex-wrap: wrap;
  }
}

@media (max-width: 720px) {
  .statsboxes_last {
    flex-wrap: wrap;
  }
}
</style>
